import React, { useState } from "react";
import { Drawer, Typography, Flex, Card, Select, Divider, Button, Input, Space, Tooltip } from "antd";
import { PhoneOutlined, MailOutlined, StarFilled, HeartFilled, SendOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { AVAILABLE_STATUSES } from "./leadsData";
import CompanyTimeline from "./CompanyTimeline";

const { Title, Text } = Typography;

function LeadDetailDrawer({
  visible,
  lead,
  company,
  onClose,
  onAddRemark,
  onStatusChange,
  onToggleStar,
  onToggleFavourite,
  availableStatuses,
  getAvailableStatusesForLead,
  onOpenMail,
}) {
  const reduxCallStatus = useSelector((state) => state.executor?.companyInfo?.callStatus);
  let baseStatuses = AVAILABLE_STATUSES;
  if (Array.isArray(reduxCallStatus) && reduxCallStatus.length > 0) {
    baseStatuses = reduxCallStatus;
  } else if (Array.isArray(availableStatuses) && availableStatuses.length > 0) {
    baseStatuses = availableStatuses;
  }
  const statusList = baseStatuses.filter((st) => {
    const norm = (st || "").toLowerCase();
    return norm !== "closed lost" && norm !== "converted";
  });

  const [remarkInput, setRemarkInput] = useState("");

  if (!lead) return null;

  const handleAddRemark = () => {
    if (!remarkInput.trim()) return;
    if (onAddRemark) onAddRemark(lead._id, remarkInput.trim());
    setRemarkInput("");
  };

  const dynamicStatuses = getAvailableStatusesForLead ? getAvailableStatusesForLead(lead) : statusList;

  return (
    <Drawer
      title={
        <Flex justify="space-between" align="center" style={{ width: "100%", paddingRight: 24 }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {lead.contactName || "Contact Details"}
            </Title>
            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
              {lead.leadCompanyName || company?.name || "Company Lead"}
            </Text>
          </div>
          <Space size="small">
            {onToggleStar && (
              <Tooltip title="Toggle Star / SPOC">
                <Button
                  type="text"
                  icon={<StarFilled style={{ color: lead.isStarred ? "#eab308" : "#cbd5e1", fontSize: 16 }} />}
                  onClick={() => onToggleStar(lead._id)}
                />
              </Tooltip>
            )}
            {onToggleFavourite && (
              <Tooltip title="Toggle Favourite">
                <Button
                  type="text"
                  icon={<HeartFilled style={{ color: lead.isFavourite ? "#ef4444" : "#cbd5e1", fontSize: 16 }} />}
                  onClick={() => onToggleFavourite(lead._id)}
                />
              </Tooltip>
            )}
          </Space>
        </Flex>
      }
      placement="right"
      width={460}
      onClose={onClose}
      open={visible}
      destroyOnClose
    >
      <Flex vertical gap={16}>
        {/* Contact Info Card */}
        <Card bordered={false} style={{ background: "#f8fafc", borderRadius: 10 }} styles={{ body: { padding: 14 } }}>
          <Flex vertical gap={10}>
            <Flex justify="space-between" align="center">
              <Text strong style={{ fontSize: "0.85rem" }}>
                Lead Status:
              </Text>
              <Select
                value={lead.status || lead.leadStatus || "New"}
                onChange={(newStatus) => onStatusChange?.(lead._id, newStatus)}
                style={{ width: 140 }}
                size="small"
                options={dynamicStatuses.map((s) => ({
                  value: s,
                  label: s,
                }))}
              />
            </Flex>

            <Divider style={{ margin: "4px 0" }} />

            <Flex align="center" justify="space-between">
              <Flex align="center" gap={8}>
                <PhoneOutlined style={{ color: "#2459d3" }} />
                <Text copyable style={{ fontSize: "0.85rem" }}>
                  {lead.contactNumber || "—"}
                </Text>
              </Flex>
            </Flex>

            <Flex align="center" justify="space-between">
              <Flex align="center" gap={8} style={{ minWidth: 0, flex: 1, paddingRight: 6 }}>
                <MailOutlined style={{ color: "#2459d3" }} />
                <Text copyable ellipsis style={{ fontSize: "0.85rem" }} title={lead.directorEmailAddress}>
                  {lead.directorEmailAddress || "No email configured"}
                </Text>
              </Flex>
              {lead.directorEmailAddress && onOpenMail && (
                <Button size="small" type="primary" ghost icon={<MailOutlined />} onClick={() => onOpenMail(lead)}>
                  Email
                </Button>
              )}
            </Flex>
          </Flex>
        </Card>

        {/* Add Remark Input */}
        <div>
          <Text strong style={{ fontSize: "0.82rem", display: "block", marginBottom: 6 }}>
            Add Follow-up Remark
          </Text>
          <Flex gap={8}>
            <Input
              size="middle"
              placeholder="Enter interaction note or remark..."
              value={remarkInput}
              onChange={(e) => setRemarkInput(e.target.value)}
              onPressEnter={handleAddRemark}
              style={{ borderRadius: 6, flex: 1, minWidth: 0 }}
            />
            <Button
              shape="circle"
              size="middle"
              type="primary"
              className="remark-send-btn"
              icon={<SendOutlined style={{ fontSize: 13, marginLeft: -1, marginTop: 1 }} />}
              onClick={handleAddRemark}
              disabled={!remarkInput.trim()}
              style={{ background: "#2459d3" }}
            />
          </Flex>
        </div>

        {/* Timeline Activities */}
        <div>
          <Text strong style={{ fontSize: "0.82rem", display: "block", marginBottom: 8 }}>
            Interaction Timeline
          </Text>
          <CompanyTimeline
            lead={lead}
            company={company}
            remarks={lead.remarks || []}
            invoices={lead.invoices || []}
            quotations={lead.quotations || []}
            proposals={lead.proposals || []}
            hideHeaderTitle
          />
        </div>
      </Flex>
    </Drawer>
  );
}

export default LeadDetailDrawer;
